/**
 * @fileoverview Modal que aparece quando o super-admin clica "Acessar Empresa"
 * numa Empresa com 2+ Contas. Lista as Contas (com busca) e dispara o switch
 * pra Conta escolhida. Empresa com 1 Conta pula este modal e entra direto.
 */

import React, { useState, useMemo, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Building2, Search, X, LogIn, ArrowRight } from "lucide-react";
import stripAccents from "../../utils/stripAccents";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    backdrop: {
      position: "fixed",
      inset: 0,
      background: dark ? "rgba(0,0,0,0.78)" : "rgba(15,23,42,0.45)",
      zIndex: 1400,
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      padding: "10vh 16px 16px",
      animation: "$fadeIn 160ms ease-out",
    },
    "@keyframes fadeIn": {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    panel: {
      width: "100%",
      maxWidth: 540,
      maxHeight: "min(80vh, 720px)",
      display: "flex",
      flexDirection: "column",
      borderRadius: 24,
      background: dark
        ? "linear-gradient(135deg, rgba(18,18,19,0.97) 0%, rgba(22,22,24,0.97) 100%)"
        : "#FFFFFF",
      border: dark ? "1px solid rgba(91,124,255,0.2)" : "1px solid rgba(15,23,42,0.08)",
      boxShadow: dark
        ? "0 24px 80px rgba(0,0,0,0.6)"
        : "0 24px 80px rgba(15,23,42,0.18)",
      overflow: "hidden",
      animation: "$slideIn 180ms cubic-bezier(0.16, 1, 0.3, 1)",
    },
    "@keyframes slideIn": {
      from: { transform: "translateY(-8px)", opacity: 0 },
      to: { transform: "translateY(0)", opacity: 1 },
    },
    header: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      padding: "20px 24px 16px",
      borderBottom: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)"}`,
    },
    iconBox: {
      width: 44,
      height: 44,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(91,124,255,0.14)" : "rgba(61,92,253,0.08)",
      color: dark ? "#7C9BFF" : "#3D5CFD",
      border: `1px solid ${dark ? "rgba(91,124,255,0.28)" : "rgba(61,92,253,0.16)"}`,
    },
    titleWrap: { flex: 1, minWidth: 0 },
    title: {
      fontSize: 17,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: "-0.3px",
      margin: 0,
    },
    subtitle: {
      fontSize: 12,
      color: dark ? "#A1A1AA" : "#71717A",
      marginTop: 2,
    },
    groupNameLabel: { fontWeight: 700, color: dark ? "#FAFAFA" : "#09090B" },
    closeBtn: {
      width: 32,
      height: 32,
      border: "none",
      background: "transparent",
      borderRadius: 8,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dark ? "#A1A1AA" : "#71717A",
      transition: "background 0.15s, color 0.15s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)" },
    },
    searchWrap: { position: "relative", padding: "12px 24px 0" },
    searchInput: {
      width: "100%",
      height: 42,
      paddingLeft: 40,
      paddingRight: 14,
      borderRadius: 10,
      background: dark ? "rgba(255,255,255,0.025)" : "#fff",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)"}`,
      color: dark ? "#FAFAFA" : "#09090B",
      fontSize: 13,
      fontFamily: "inherit",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color .15s, box-shadow .15s",
      "&:focus": {
        borderColor: dark ? "#5B7CFF" : "#3D5CFD",
        boxShadow: dark
          ? "0 0 0 1px rgba(91,124,255,0.15)"
          : "0 0 0 1px rgba(61,92,253,0.12)",
      },
      "&::placeholder": { color: dark ? "#71717A" : "#A1A1AA" },
    },
    searchIcon: {
      position: "absolute",
      left: 36,
      top: "50%",
      transform: "translateY(-50%)",
      color: dark ? "#71717A" : "#A1A1AA",
      pointerEvents: "none",
      display: "flex",
    },
    list: {
      flex: 1,
      overflowY: "auto",
      padding: "12px 14px 16px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      ...theme.scrollbarStylesSoft,
    },
    accountRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 14px",
      borderRadius: 12,
      cursor: "pointer",
      border: `1px solid transparent`,
      background: "transparent",
      transition: "background .12s, border-color .12s",
      fontFamily: "inherit",
      width: "100%",
      textAlign: "left",
      "&:hover:not(:disabled)": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
        borderColor: dark ? "rgba(91,124,255,0.22)" : "rgba(61,92,253,0.16)",
      },
      "&:disabled": { opacity: 0.55, cursor: "wait" },
    },
    accountIconBox: {
      width: 36,
      height: 36,
      borderRadius: 10,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: dark ? "rgba(91,124,255,0.10)" : "rgba(61,92,253,0.06)",
      color: dark ? "#7C9BFF" : "#3D5CFD",
    },
    accountWrap: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
    },
    accountName: {
      fontSize: 14,
      fontWeight: 600,
      color: dark ? "#FAFAFA" : "#09090B",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    accountMeta: {
      fontSize: 11,
      color: dark ? "#71717A" : "#A1A1AA",
      marginTop: 2,
    },
    chevron: {
      color: dark ? "#71717A" : "#A1A1AA",
      flexShrink: 0,
    },
    empty: {
      textAlign: "center",
      padding: 40,
      color: dark ? "#71717A" : "#A1A1AA",
      fontSize: 13,
    },
  };
});

const AccountSelectorModal = ({ open, onClose, group, onSelect }) => {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const [pickingId, setPickingId] = useState(null);

  // Reset estado quando modal abre/fecha.
  useEffect(() => {
    if (!open) {
      setSearch("");
      setPickingId(null);
    }
  }, [open]);

  // ESC fecha. Mantém o foco do input quando abre.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" && !pickingId && onClose) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pickingId, onClose]);

  // Sem diferença de acento e de espaço nas pontas, como as buscas da tela de Empresas.
  const filtered = useMemo(() => {
    if (!group?.accounts?.length) return [];
    const q = stripAccents(search.trim().toLowerCase());
    if (!q) return group.accounts;
    return group.accounts.filter(c => stripAccents((c.name || "").toLowerCase()).includes(q));
  }, [group, search]);

  if (!open || !group) return null;

  const handlePick = async (account) => {
    if (pickingId) return;
    setPickingId(account.id);
    try {
      // A troca que falha devolve { error } (o aviso já apareceu): a lista
      // volta a aceitar escolha e o modal volta a fechar.
      const result = onSelect ? await onSelect(account) : null;
      if (result?.error) setPickingId(null);
    } catch {
      setPickingId(null);
    }
  };

  return (
    <div
      className={classes.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget && !pickingId && onClose) onClose();
      }}
    >
      <div className={classes.panel} role="dialog" aria-modal="true">
        <header className={classes.header}>
          <div className={classes.iconBox}>
            <Building2 size={20} strokeWidth={1.75} />
          </div>
          <div className={classes.titleWrap}>
            <h2 className={classes.title}>Escolha a Conta</h2>
            <div className={classes.subtitle}>
              Empresa <span className={classes.groupNameLabel}>{group.displayName}</span> ·{" "}
              {group.accounts.length} contas
            </div>
          </div>
          <button
            type="button"
            className={classes.closeBtn}
            onClick={() => { if (!pickingId && onClose) onClose(); }}
            aria-label="Fechar"
            disabled={!!pickingId}
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        <div className={classes.searchWrap}>
          <span className={classes.searchIcon}>
            <Search size={15} />
          </span>
          <input
            type="search"
            autoComplete="off"
            className={classes.searchInput}
            placeholder="Buscar conta pelo nome..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div className={classes.list}>
          {filtered.length === 0 ? (
            <div className={classes.empty}>
              {search ? "Nenhuma conta encontrada." : "Esta empresa não tem contas."}
            </div>
          ) : (
            filtered.map((account) => {
              const isPicking = pickingId === account.id;
              return (
                <button
                  key={account.id}
                  type="button"
                  className={classes.accountRow}
                  onClick={() => handlePick(account)}
                  disabled={!!pickingId}
                  aria-label={`Acessar conta ${account.name}`}
                >
                  <div className={classes.accountIconBox}>
                    <Building2 size={16} strokeWidth={1.75} />
                  </div>
                  <div className={classes.accountWrap}>
                    <span className={classes.accountName} title={account.name}>
                      {account.name}
                    </span>
                    <span className={classes.accountMeta}>
                      {isPicking ? "Acessando..." : "Clique para acessar"}
                    </span>
                  </div>
                  {isPicking ? (
                    <LogIn size={16} strokeWidth={2} className={classes.chevron} />
                  ) : (
                    <ArrowRight size={16} strokeWidth={2} className={classes.chevron} />
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSelectorModal;
